import { Metadata } from 'next';

// The single source of truth for name/domain/email/social — every other file that needs
// one of these should import siteConfig rather than typing the literal again. Domain/email/
// social stay env-backed so a domain move is "set one env var," not "grep the repo": see
// the Brand Identity Audit for the ~60 files that were hardcoding these independently
// before this existed, and .env.seo.example for where NEXT_PUBLIC_BASE_URL is documented
// alongside the other SEO env vars.
//
// The brand name is deliberately a hard constant, NOT env-backed: the "Print With Magic" →
// "Ink With Magic" rebrand needs to land from the code alone, without depending on a
// NEXT_PUBLIC_SITE_NAME value being cleared in every deploy environment's dashboard. A
// stale env override there would silently revert the name everywhere it renders.
const SITE_NAME = 'Ink With Magic';
const SITE_URL = (process.env.NEXT_PUBLIC_BASE_URL || 'https://inkwithmagic.com').replace(/\/+$/, '');
// Domain alone (no protocol) — for building addr@domain and social handles below without
// re-deriving it from SITE_URL at every call site.
const SITE_DOMAIN = SITE_URL.replace(/^https?:\/\//, '');

export const siteConfig = {
  name: SITE_NAME,
  title: 'Personalized Gifts',
  description: 'Turn your photos into a personalized AI comic book. Make your loved ones the heroes of their own story with recognizable characters and professional printing. The perfect unique gift for birthdays, anniversaries, and special milestones.',
  url: SITE_URL,
  ogImage: `${SITE_URL}/images/og-image.jpg`,
  // One address per purpose, not one address reused everywhere — see the audit for which
  // page/flow uses which. Override individually only if a rebrand needs to split them
  // differently; most rebrands just change NEXT_PUBLIC_BASE_URL and get all four for free.
  emails: {
    general: process.env.NEXT_PUBLIC_EMAIL_GENERAL || `hey@${SITE_DOMAIN}`,
    // Matches `general`'s default on purpose — the published legal pages
    // (return-policy.md, terms-services.md, privacy-policy.md) and the vast
    // majority of outbound mail already use hey@; `support` defaulting to a
    // different address (hi@) was an inconsistency, not a deliberate split.
    // Override independently if these ever need to diverge for real.
    support: process.env.NEXT_PUBLIC_EMAIL_SUPPORT || `hey@${SITE_DOMAIN}`,
    skip: process.env.NEXT_PUBLIC_EMAIL_SKIP || `skip@${SITE_DOMAIN}`,
    orders: process.env.NEXT_PUBLIC_EMAIL_ORDERS || `orders@${SITE_DOMAIN}`,
  },
  social: {
    instagram: process.env.NEXT_PUBLIC_INSTAGRAM_HANDLE || 'inkwithmagic',
    instagramUrl: `https://www.instagram.com/${process.env.NEXT_PUBLIC_INSTAGRAM_HANDLE || 'inkwithmagic'}/`,
    twitterHandle: process.env.NEXT_PUBLIC_TWITTER_HANDLE || 'inkwithmagic',
    facebookUrl: process.env.NEXT_PUBLIC_FACEBOOK_URL || 'https://www.facebook.com/inkwithmagic/',
  },
  keywords: [
    // === COMIC BOOKS (Primary - High Intent) ===
    'personalized comic book',
    'custom comic book',
    'comic book gift',
    'create comic book from photos',
    'make your own comic book',
    'personalized comic book gift',
    'custom comic book with photos',
    'ai comic book',
    'ai generated comic book',
    'photo comic book',
    'comic book from photos',
    'personalized superhero comic book',
    'custom story comic book',
    'comic book for kids',
    'comic book birthday gift',
    'comic book anniversary gift',
    'unique comic book gift',
    'funny comic book gift',
    'printed ai comic book',
    'ai comic with consistent characters',
    'custom comic with recognizable faces',
    'turn photos into comic book',
    'make someone a superhero comic',
    'personalized gift for husband comic',
    'personalized gift for wife comic',
    'milestone birthday comic book',
    'couple story comic book',
    'personalized gifts for him',
    'personalized gifts for her',
    'personalized gifts for kids',
    'personalized gifts for couples',
    'personalized gifts for best friend',
    'funny personalized gifts',
    'unique personalized gifts',
    'custom gifts from photos',
    'ai personalized gifts',
    'magic comic books',
    'comic book printing',

    // === PERSONALIZED GIFTS (Shared) ===
    'personalized gifts',
    'custom gifts',
    'personalized photo gifts',
    'unique gifts',
    'thoughtful gifts',
    'meaningful gifts',
    'memorable gifts',
    'sentimental gifts',
    'gifts for family',
    'gifts for friends',
    'gifts for mom',
    'gifts for dad',
    'gifts for grandma',
    'gifts for grandpa',
    'gifts for kids',
    'gifts for couples',
    'birthday gifts',
    'anniversary gifts',
    'holiday gifts',
  ],
};

export const defaultMetadata: Metadata = {
  metadataBase: new URL(siteConfig.url),
  title: {
    default: siteConfig.title,
    template: `%s | ${siteConfig.name}`
  },
  description: siteConfig.description,
  keywords: siteConfig.keywords,
  authors: [{ name: siteConfig.name }],
  creator: siteConfig.name,
  publisher: siteConfig.name,
  robots: {
    index: true,
    follow: true,
    googleBot: {
      index: true,
      follow: true,
      'max-video-preview': -1,
      'max-image-preview': 'large',
      'max-snippet': -1,
    },
  },
  openGraph: {
    type: 'website',
    locale: 'en_US',
    url: siteConfig.url,
    title: siteConfig.title,
    description: siteConfig.description,
    siteName: siteConfig.name,
    images: [
      {
        url: siteConfig.ogImage,
        width: 1200,
        height: 630,
        alt: `${siteConfig.name} - Personalized AI Comic Books`,
      },
    ],
  },
  twitter: {
    card: 'summary_large_image',
    title: siteConfig.title,
    description: siteConfig.description,
    images: [siteConfig.ogImage],
    creator: `@${siteConfig.social.twitterHandle}`,
  },
  alternates: {
    canonical: siteConfig.url,
  },
  verification: {
    google: '22QNBaDUVC9WjFf0mavbpCdShe_8hzOyoPnA0-FdU2o',
  },
};

export const pageMetadata = {
  // Comic book pages (primary)
  comix: {
    title: 'Personalized Gifts | Magic Comic Books',
    description: 'Turn your photos into a personalized comic book! Create hilarious, custom comic books with AI from your family photos. Perfect gift for any occasion.',
    keywords: ['personalized comic book', 'custom comic book', 'ai comic book', 'photo comic book', 'comic book gift'],
  },
  comixCreate: {
    title: 'Create Your Comic Book | Upload Your Photos',
    description: 'Upload your photos and create a personalized AI comic book in minutes. Choose your characters, pick a story, and get a printed comic book delivered to your door.',
    keywords: ['create comic book', 'upload photos comic', 'ai comic book maker', 'custom comic book creator'],
  },
  comixPreview: {
    title: 'Preview Your Comic Book | Magic Comic Books',
    description: 'Preview your personalized AI comic book before ordering. See your characters come to life in a professionally printed comic book.',
    keywords: ['preview comic book', 'comic book preview', 'personalized comic preview'],
  },
};
